/* eslint-disable @typescript-eslint/no-explicit-any */
import i18n from '@/plugins/i18n'
import Prismic from 'prismic-javascript'
import prismicDom from 'prismic-dom'
import { CatalogEntry, CustomTermsEntry } from '@/types/prismic'

const PrismicApi = Prismic.api(`${process.env.VUE_APP_API_PRISMIC}`)

const getPrismicText = prismicDom.RichText.asText

const _getCMSContent = (
  type: string,
  tag: string,
  lang: string = i18n.locale
) => {
  const response = PrismicApi.then(function get(api) {
    return api.query(
      [
        Prismic.Predicates.at('document.type', type),
        Prismic.Predicates.at('document.tags', [tag])
      ],
      { lang }
    )
  })

  return response
}

const PRISMIC_CATALOG_TTL_MS = 300000

const mapPrismicOptionsToCatalog = (prismicResponse: any): CatalogEntry[] => {
  if (!prismicResponse.results?.length) return []

  return prismicResponse.results[0].data.options.map((prismicEntry: any) => {
    return {
      externalKey: getPrismicText(prismicEntry.externalkey),
      title: getPrismicText(prismicEntry.title),
      description: getPrismicText(prismicEntry.description),
      descriptionShort: getPrismicText(prismicEntry['description-short']),
      iconActiveUrl: prismicEntry.iconactive.url,
      iconDisabledUrl: prismicEntry.icondisabled.url
    }
  })
}

function createCachedCatalogLoader(type: string, tag: string) {
  let cache: { lang: string; data: CatalogEntry[]; expires: number } | null =
    null
  let inFlight: Promise<CatalogEntry[]> | null = null
  let inFlightLang: string | null = null

  return async function getCatalog(): Promise<CatalogEntry[]> {
    const lang = i18n.locale
    const now = Date.now()

    if (
      cache &&
      cache.lang === lang &&
      cache.expires > now
    ) {
      return cache.data
    }

    if (inFlight && inFlightLang === lang) {
      return inFlight
    }

    inFlightLang = lang
    inFlight = (async () => {
      const prismicResponse = await _getCMSContent(type, tag, lang)
      const data = mapPrismicOptionsToCatalog(prismicResponse)
      cache = {
        lang,
        data,
        expires: Date.now() + PRISMIC_CATALOG_TTL_MS
      }
      return data
    })().finally(() => {
      inFlight = null
      inFlightLang = null
    })

    return inFlight
  }
}

const getProductCatalogCached = createCachedCatalogLoader(
  'resources',
  'catalog'
)
const getAppCatalogCached = createCachedCatalogLoader(
  'resources',
  'app_catalog'
)

export default {
  getProductCatalog: getProductCatalogCached,

  getAppCatalog: getAppCatalogCached,

  async getCustomTerms(): Promise<CustomTermsEntry[]> {
    const prismicResponse = await _getCMSContent('customterms', 'customTerms')

    if (!prismicResponse.results || !prismicResponse.results.length) return []

    return prismicResponse.results[0].data.productterms
      .map((prismicEntry: any) => {
        return {
          productSku: prismicEntry.productsku,
          termsConfirmation: getPrismicText(prismicEntry.termsconfirmation),
          termsText: getPrismicText(prismicEntry.termstext)
        }
      })
      .filter(
        (terms: CustomTermsEntry) => terms.termsConfirmation && terms.termsText
      )
  },

  getAllConfiuredResources() {
    return PrismicApi.then(api => api.getSingle('resources'))
  },

  loadLangContent(lang: string) {
    return PrismicApi.then(function(api) {
      return api
        .query(Prismic.Predicates.at('document.type', 'content'), {
          lang,
          pageSize: 100
        })
        .then(response => {
          return Object.assign(
            {},
            ...response.results.map(item => ({
              [item.tags[0]]: Object.assign(
                {},
                {
                  txt: Object.assign(
                    {},
                    ...item.data.labels.map(
                      (elem: {
                        externalkey: { text: string }[]
                        label: { text: string }[]
                      }) => ({
                        [elem.externalkey[0].text]: elem.label[0].text
                      })
                    )
                  ),
                  img: Object.assign(
                    {},
                    ...item.data.images.map(
                      (elem: {
                        externalkey: { text: string }[]
                        alt: { text: string }[]
                        img: { url: string }
                        disabled: { url: string }
                      }) => ({
                        [elem.externalkey[0].text]: Object.assign(
                          {},
                          {
                            alt: elem.alt[0].text,
                            img: elem.img.url,
                            disabled: elem.disabled.url
                          }
                        )
                      })
                    )
                  )
                }
              )
            }))
          )
        })
    })
  }
}
